<template>
    <div
        @focusin="onFocus"
        @focusout="onFocusout"
    >
        <show-all-new
            :title="pageTitle"
            :items="listItems"
            :is-loading="isLoading"
            @item-selected="itemsSelected"
            @request-more="fetchMoreItems"
        />
    </div>
</template>

<script setup lang="ts">
    import type { OnDemandCardData } from '@/components/Cards/types';
    import { Strings } from '@/locales/types/enums';
    import { linkItems } from "@/components/Header/types";
    import { PlayItem } from '@ca.vmedia.tv/domains';
    import { useI18n } from "vue-i18n";
    import { useShowAllMyTvOnDemandStore } from "@/stores/ShowAllMyTvOnDemand";
    import { storeToRefs } from 'pinia';
    import  { 
        type NavigationGuardNext, 
        type RouteLocationNormalizedGeneric, 
        type RouteLocationNormalizedLoadedGeneric, 
        useRouter,
        onBeforeRouteLeave 
    } from "vue-router";
    import { useDetailPageStore } from "@/stores/DetailPage";
    import type { IEventSelected } from "@/components/MultiLineView/types";
    import { useApplicationStore } from "@/stores/Application";

    const {
        setViewedDetailItems,
    } = useDetailPageStore();

    definePageMeta({ 
        layout: 'default-no-header',
        middleware: ['set-page-indicator'],
    });

    const ApplicationStore = useApplicationStore();
    const {
        setTooltipeShowing,
        setTooltipeMessage
    } = ApplicationStore;

    const ShowAllMyTvOnDemandStore = useShowAllMyTvOnDemandStore();
    const {
        loadFavoritesOnDemandNextPage,
        clearAll
    } = ShowAllMyTvOnDemandStore;
    const {
        getCardsOnDemand: listItems,
        getIsFavoritesOnDemandLoading: isLoading,
    } = storeToRefs(ShowAllMyTvOnDemandStore);


    const router = useRouter();
    const { $navigator } = useNuxtApp();
    const { t } = useI18n();
    const pageTitle: string = t(Strings.MY_TV_FAVORITE_ON_DEMAND).toString();

    const fetchMoreItems =  async (): Promise<void> => {
        await loadFavoritesOnDemandNextPage();
    };
    const itemsSelected = (event: IEventSelected) => {
        try {
            const item = event.item as OnDemandCardData;
            const playItem = item?.getPlayItem();
            if (!playItem) {
                throw new Error("playItem is null:" + JSON.stringify(event.item));
            }

            setViewedDetailItems(playItem);
            const path = PlayItem.isSeries(playItem) ?
                `${linkItems.detailsVodSeries.routerPath}/${playItem.id}` :
                `${linkItems.detailsVodMovies.routerPath}/${playItem.id}`;
            void router.push(path);
        } catch (error) {
            Logger.captureError(error as Error);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true});
        }
    };

    const onFocus = () => {
        registerKeyListeners();
    };
    const onFocusout = () => {
        unregisterKeyListeners();
    };
    const keyBackListener = (): void => {
        router.go(-1);
    };
    const registerKeyListeners = (): void => {
        $navigator.on("keypress:back", keyBackListener);
    };
    const unregisterKeyListeners = (): void => {
        $navigator.off("keypress:back", keyBackListener);
    };

    onBeforeRouteLeave((to: RouteLocationNormalizedGeneric, _from: RouteLocationNormalizedLoadedGeneric, next: NavigationGuardNext) => {
        if(to.path === linkItems.myTv.routerPath) {
            clearAll();
        }
        next();
    });
</script>
